import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ContentProposalVM, MediaRowVM, MediaState } from "../api";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const card = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)" } as const;

const KIND: Record<string, { bg: string; fg: string; label: string }> = {
  update: { bg: "#e8f0f3", fg: "#2a6f8a", label: "Update" },
  new: { bg: "#e8f4ee", fg: "#1f7a4d", label: "New" },
  video: { bg: "#f1e9f7", fg: "#6b3fa0", label: "Video" },
  retire: { bg: "#f7e4e0", fg: "#b3261e", label: "Retire" },
};
const STATE: Record<string, { bg: string; fg: string }> = {
  suggested: { bg: "#f0efe9", fg: "#6b706a" },
  drafted: { bg: "#e8eef7", fg: "#2a5f9e" },
  review: { bg: "#fbf2dc", fg: "#9a6a12" },
  scheduled: { bg: "#e8f4ee", fg: "#1f7a4d" },
};
const POLICY_TONE: Record<string, { bg: string; fg: string; label: string }> = {
  auto: { bg: "#e8f4ee", fg: "#1f8a5b", label: "Auto-publish" },
  review: { bg: "#fbf2dc", fg: "#9a6a12", label: "Human review" },
  manual: { bg: "#fbe9e7", fg: "#b3261e", label: "Human writes it" },
};

// Video-pipeline media states (V3_README §9). `draft` is produced-but-unreviewed;
// a video can't publish until everything is `ready` or `not_proposed`.
const MEDIA_STATE_TONE: Record<MediaState, { bg: string; fg: string; label: string }> = {
  not_proposed: { bg: "#f0efe9", fg: "#6b706a", label: "Not proposed" },
  pending: { bg: "#eef0f5", fg: "#5b6270", label: "Pending" },
  draft: { bg: "#fbf2dc", fg: "#9a6a12", label: "Draft · needs review" },
  ready: { bg: "#e8f4ee", fg: "#1f8a5b", label: "Ready" },
  needs_rerecord: { bg: "#fbe9e7", fg: "#b3261e", label: "Needs re-record" },
};
// Allowed next states + button label — mirrors the server state machine
// (content.ts MEDIA_TRANSITIONS); the server re-validates, this is only the UI.
const MEDIA_NEXT: Record<MediaState, { to: MediaState; label: string }[]> = {
  pending: [{ to: "draft", label: "Mark drafted" }, { to: "not_proposed", label: "Not needed" }],
  draft: [{ to: "ready", label: "Mark ready" }, { to: "pending", label: "Re-queue" }],
  ready: [{ to: "needs_rerecord", label: "Request re-record" }],
  needs_rerecord: [{ to: "pending", label: "Re-queue" }],
  not_proposed: [{ to: "pending", label: "Add it" }],
};
const MEDIA_KIND_LABEL: Record<string, string> = { screenshot: "Screenshot", video: "Video", storyboard: "Storyboard", voiceover: "Voiceover", captions: "Captions" };

// A video may publish only when every media row is terminal (ready|not_proposed).
function mediaReady(rows: MediaRowVM[]): boolean {
  return rows.length > 0 && rows.every((m) => m.state === "ready" || m.state === "not_proposed");
}

// DiffView (COMPONENTS.md) — add / cut / keep with a mono marker column.
function DiffView({ diff }: { diff: [string, string][] }) {
  return (
    <div style={{ border: "1px solid #f0efe9", borderRadius: 3, overflow: "hidden", fontSize: 12.5, lineHeight: 1.5 }}>
      {diff.map(([op, text], i) => {
        const add = op === "add", cut = op === "cut";
        const bg = add ? "#eef7f1" : cut ? "#fbe9e7" : "transparent";
        const fg = add ? "#1f6d48" : cut ? "#8a2f22" : "#4a504a";
        const mark = add ? "+" : cut ? "−" : " ";
        return (
          <div key={i} style={{ display: "flex", gap: 8, background: bg, padding: "5px 10px", borderTop: i ? "1px solid #f4f3ee" : "none" }}>
            <span style={{ width: 12, flex: "none", fontFamily: "'Space Mono',monospace", color: fg, textAlign: "center" }}>{mark}</span>
            <span style={{ color: fg, textDecoration: cut ? "line-through" : "none", textWrap: "pretty" }}>{text}</span>
          </div>
        );
      })}
    </div>
  );
}

function ProposalCard({ p }: { p: ContentProposalVM }) {
  const k = KIND[p.kind] ?? KIND.update!;
  const s = STATE[p.state] ?? STATE.suggested!;
  return (
    <div style={{ ...card, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: k.bg, color: k.fg }}>{k.label}</span>
        <span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: s.bg, color: s.fg, textTransform: "capitalize" }}>{p.state}</span>
        <span style={{ fontSize: 11.5, color: "#8a8e88" }}>{p.source} · {p.scope}</span>
        <span style={{ flex: 1 }} />
        {p.autoPublishable ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: "#e8f4ee", color: "#1f8a5b" }}>
            <Icon name="sparkles" size={11} color="#1f8a5b" />Publishes automatically
          </span>
        ) : (
          <span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: "#f0efe9", color: "#6b706a" }}>Needs a human</span>
        )}
        <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5, color: "#8a8e88" }}>{p.confidence}%</span>
      </div>

      <div style={{ fontSize: 15, fontWeight: 600, color: "#1d241f" }}>{p.articleTitle}</div>
      <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 2 }}>{p.trigger}{p.articleCategory ? ` · ${p.articleCategory}` : ""}</div>

      {/* rationale — always shown to the reviewer */}
      <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a504a", margin: "10px 0", textWrap: "pretty" }}>{p.rationale}</div>

      {p.diff.length > 0 && <DiffView diff={p.diff} />}

      {p.mediaRows.length > 0 ? (
        <MediaPanel proposalId={p.id} rows={p.mediaRows} />
      ) : p.media.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#a8aca6", marginBottom: 6 }}>Media implications</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {p.media.map(([kind, what, note], i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12.5 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#6b3fa0", background: "#f1e9f7", borderRadius: 2, padding: "1px 6px", flex: "none" }}>{kind}</span>
                <span style={{ color: "#1d241f" }}>{what}</span>
                {note && <span style={{ color: "#8a8e88" }}>— {note}</span>}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <ProposalActions p={p} />
    </div>
  );
}

const mediaBtn = { border: "1px solid #dcdad2", background: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11.5, fontWeight: 600, color: "#4a504a", flex: "none" } as const;

// MediaRow — one production artifact: its state, and the staff controls that drive
// it (V3_README §9 / DECISIONS.md Q6). A `video` row also needs the produced clip's
// asset reference attached before it can be marked ready.
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024; // Cloudflare basic direct-upload cap

function MediaRow({ proposalId, m }: { proposalId: string; m: MediaRowVM }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(m.encoding ? -1 : null); // null idle; 0–100 uploading; -1 encoding
  const fileRef = useRef<HTMLInputElement | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const alive = useRef(true);
  // Set true on (re)mount too, so React 18 StrictMode's mount→unmount→remount in
  // dev re-arms the guard; abort any in-flight upload on unmount.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      xhrRef.current?.abort();
    };
  }, []);
  const mut = useMutation({
    mutationFn: (patch: { state?: MediaState; assetRef?: string }) => api.transitionMedia(proposalId, m.id, patch),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ["contentStudio"] }); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Could not update media"),
  });

  // Poll the server (which reads the row's own pending uid) until Stream finishes
  // encoding; aborts on unmount so it never setState after teardown or run forever.
  async function pollUntilReady() {
    let fails = 0;
    for (let i = 0; i < 100 && alive.current; i++) {
      try {
        const st = await api.streamStatus(proposalId, m.id);
        if (!alive.current) return;
        if (st.ready) {
          setUploadPct(null);
          qc.invalidateQueries({ queryKey: ["contentStudio"] });
          return;
        }
        if (st.error) {
          setErr("Encoding failed — try uploading again.");
          setUploadPct(null);
          return;
        }
        fails = 0; // a clean not-ready response resets the failure streak
      } catch {
        // A persistent HTTP error (e.g. the pending upload is gone) must stop the
        // poll, not spin for minutes; a lone transient blip is retried.
        if (!alive.current) return;
        if (++fails >= 3) {
          setErr("Lost contact while checking the upload — reload to retry.");
          setUploadPct(null);
          return;
        }
      }
      await new Promise((r) => setTimeout(r, 3000));
    }
    if (alive.current) setUploadPct((p) => (p === -1 ? null : p)); // stop the spinner; the pending upload resumes on next open
  }

  // Resume polling a clip that was still encoding when the studio (re)loaded — the
  // pending uid lives server-side, so a slow encode is never orphaned.
  useEffect(() => {
    if (m.encoding) void pollUntilReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.encoding]);

  // Upload a produced clip to Cloudflare Stream: mint a direct upload off the
  // Stream binding, POST the file straight to Stream (basic multipart, ≤200MB),
  // then poll until the encode is ready.
  async function onFile(file: File) {
    setErr(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setErr("That file is over the 200 MB limit — please upload a smaller clip.");
      return;
    }
    setUploadPct(0);
    try {
      const { uploadURL } = await api.streamUpload(proposalId, m.id, file.size);
      await new Promise<void>((resolve, reject) => {
        const form = new FormData();
        form.append("file", file);
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open("POST", uploadURL);
        xhr.timeout = 20 * 60 * 1000; // a stalled upload must not spin forever
        xhr.upload.onprogress = (e) => { if (e.lengthComputable && alive.current) setUploadPct(Math.floor((e.loaded / e.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Upload failed")));
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.ontimeout = () => reject(new Error("Upload timed out — try again."));
        xhr.onabort = () => reject(new Error("aborted"));
        xhr.send(form);
      }).finally(() => { xhrRef.current = null; });
      if (!alive.current) return;
      setUploadPct(-1); // uploaded — Stream encodes; poll for readiness
      await pollUntilReady();
    } catch (e) {
      if (!alive.current) return;
      setErr(e instanceof Error ? e.message : "Upload failed");
      setUploadPct(null);
    }
  }

  const tone = MEDIA_STATE_TONE[m.state];
  const isVideo = m.kind === "video";
  const uploading = uploadPct !== null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12.5 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "#6b3fa0", background: "#f1e9f7", borderRadius: 2, padding: "1px 6px", flex: "none" }}>{MEDIA_KIND_LABEL[m.kind] ?? m.kind}</span>
      <span style={{ color: "#1d241f" }}>{m.label}</span>
      {m.chapterRef && <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, color: "#8a8e88" }}>· {m.chapterRef}</span>}
      {m.note && <span style={{ color: "#8a8e88" }}>— {m.note}</span>}
      {isVideo && m.assetRef && <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, color: "#1f8a5b" }}>clip · {m.assetRef}</span>}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: tone.bg, color: tone.fg, flex: "none" }}>{tone.label}</span>
      {/* A video needs its produced clip uploaded to Cloudflare Stream before it can
          be marked ready. The clip is locked once ready — to swap it, move the row
          out of ready first. */}
      {isVideo && m.state !== "ready" && (
        <>
          <input ref={fileRef} type="file" accept="video/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
          {uploading ? (
            <span style={{ fontSize: 11.5, color: "#8a8e88", fontFamily: "'Space Mono',monospace" }}>{uploadPct === -1 ? "Encoding…" : `Uploading ${uploadPct}%`}</span>
          ) : (
            <button onClick={() => fileRef.current?.click()} disabled={mut.isPending} style={{ ...mediaBtn, cursor: mut.isPending ? "not-allowed" : "pointer" }}>{m.assetRef ? "Replace clip" : "Upload clip"}</button>
          )}
        </>
      )}
      {MEDIA_NEXT[m.state].map((t) => {
        const needsClip = isVideo && t.to === "ready" && !m.assetRef;
        const disabled = mut.isPending || needsClip;
        // Marking a video ready asserts the exact clip being reviewed (the ref shown
        // here), so a clip swapped in after this loaded can't be approved as reviewed.
        const patch = isVideo && t.to === "ready" ? { state: t.to, expectedAssetRef: m.assetRef ?? undefined } : { state: t.to };
        return (
          <button key={t.to} onClick={() => mut.mutate(patch)} disabled={disabled} title={needsClip ? "Attach the produced clip first" : undefined} style={{ ...mediaBtn, cursor: disabled ? "not-allowed" : "pointer", color: disabled ? "#a8aca6" : "#4a504a" }}>{t.label}</button>
        );
      })}
      {err && <span style={{ color: "#b3261e", fontSize: 11 }}>{err}</span>}
    </div>
  );
}

// MediaPanel — the video pipeline's production rows (V3_README §9).
function MediaPanel({ proposalId, rows }: { proposalId: string; rows: MediaRowVM[] }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#a8aca6", marginBottom: 6 }}>Media production</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {rows.map((m) => <MediaRow key={m.id} proposalId={proposalId} m={m} />)}
      </div>
    </div>
  );
}

function ProposalActions({ p }: { p: ContentProposalVM }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const done = () => qc.invalidateQueries({ queryKey: ["contentStudio"] });
  const publish = useMutation({
    mutationFn: () => api.resolveProposal(p.id, "publish"),
    onSuccess: done,
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Could not publish"),
  });
  const reject = useMutation({
    mutationFn: () => api.resolveProposal(p.id, "reject", reason.trim()),
    onSuccess: done,
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Could not reject"),
  });
  const busy = publish.isPending || reject.isPending;
  // A video can't publish until every media row is human-reviewed (V3_README §9);
  // the server enforces this too (video_media_not_ready), this just guides the UI.
  const videoBlocked = p.kind === "video" && !mediaReady(p.mediaRows);
  const canPublish = !busy && !videoBlocked;

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f4f3ee" }}>
      {!rejecting ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
          <span style={{ color: "#8a8e88" }}>Reviewer · <span style={{ color: "#1d241f", fontWeight: 600 }}>{p.reviewer}</span></span>
          <span style={{ flex: 1 }} />
          {videoBlocked && <span style={{ color: "#9a6a12", fontSize: 11.5 }}>Media not yet reviewed</span>}
          {err && <span style={{ color: "#b3261e", fontSize: 11.5 }}>{err}</span>}
          <button onClick={() => setRejecting(true)} disabled={busy} style={{ border: "1px solid #dcdad2", background: "#fff", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#b3261e", cursor: busy ? "not-allowed" : "pointer" }}>Reject</button>
          <button onClick={() => publish.mutate()} disabled={!canPublish} title={videoBlocked ? "Every storyboard, voiceover and caption must be marked ready first" : undefined} style={{ border: 0, background: canPublish ? "#006747" : "#f0efe9", color: canPublish ? "#fff" : "#8a8e88", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: canPublish ? "pointer" : "not-allowed" }}>{publish.isPending ? "Publishing…" : "Publish"}</button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11.5, color: "#8a8e88" }}>A rejection reason is required — it's the pipeline's only training signal.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being rejected?" style={{ flex: 1, padding: "8px 11px", border: "1px solid #dcdad2", borderRadius: 6, fontSize: 13, outline: 0 }} />
            <button onClick={() => setRejecting(false)} style={{ border: "1px solid #dcdad2", background: "#fff", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#4a504a", cursor: "pointer" }}>Cancel</button>
            <button onClick={() => reject.mutate()} disabled={!reason.trim() || busy} style={{ border: 0, background: reason.trim() && !busy ? "#b3261e" : "#f0efe9", color: reason.trim() && !busy ? "#fff" : "#8a8e88", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: reason.trim() && !busy ? "pointer" : "not-allowed" }}>{reject.isPending ? "Rejecting…" : "Reject proposal"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ContentStudio() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["contentStudio"], queryFn: api.contentStudio });
  const generate = useMutation({ mutationFn: api.generateContent, onSuccess: () => qc.invalidateQueries({ queryKey: ["contentStudio"] }) });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Content studio</h1>
          <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "#8a8e88", textWrap: "pretty" }}>Where all knowledge is authored, and where the system proposes work. Every article a customer reads originates here and is pushed down.</p>
        </div>
        <button onClick={() => generate.mutate()} disabled={generate.isPending} style={{ border: 0, background: generate.isPending ? "#f0efe9" : "#006747", color: generate.isPending ? "#8a8e88" : "#fff", borderRadius: 6, padding: "9px 16px", fontSize: 12.5, fontWeight: 600, cursor: generate.isPending ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 7, flex: "none" }}>
          <Icon name="sparkles" size={15} color={generate.isPending ? "#8a8e88" : "#fff"} />
          {generate.isPending ? "Scanning sources…" : "Generate from sources"}
        </button>
      </div>

      {/* Watched sources */}
      <div style={{ ...card, padding: "12px 16px" }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#a8aca6", marginBottom: 10 }}>Watched sources</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {data.sources.map((s) => (
            <span key={s.source} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, border: "1px solid #eceae3", borderRadius: 999, padding: "5px 12px", color: "#4a504a" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.enabled ? "#1f8a5b" : "#bcbfba" }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Generation queue */}
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#a8aca6", margin: "4px 0 10px" }}>Generation queue · {data.queue.length}</div>
        {data.queue.length === 0 ? (
          <div style={{ ...card, padding: "34px 20px", textAlign: "center", color: "#8a8e88", fontSize: 13.5 }}>Nothing proposed right now. As the watched sources fire, proposals land here.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {data.queue.map((p) => <ProposalCard key={p.id} p={p} />)}
          </div>
        )}
      </div>

      {/* Policy table */}
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#a8aca6", margin: "4px 0 10px" }}>What may publish itself</div>
        <div style={{ ...card, overflow: "hidden" }}>
          {data.policy.map((row, i) => {
            const t = POLICY_TONE[row.tier] ?? POLICY_TONE.review!;
            return (
              <div key={row.change} style={{ display: "grid", gridTemplateColumns: "minmax(180px,1.4fr) minmax(140px,1fr) minmax(220px,2fr)", gap: 12, padding: "11px 16px", borderTop: i ? "1px solid #f4f3ee" : "none", alignItems: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1d241f" }}>{row.change}</div>
                <div><span style={{ fontSize: 10.5, fontWeight: 600, borderRadius: 2, padding: "2px 8px", background: t.bg, color: t.fg }}>{t.label}</span></div>
                <div style={{ fontSize: 12.5, color: "#8a8e88", textWrap: "pretty" }}>{row.detail}</div>
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 8, textWrap: "pretty" }}>The auto-publish verdict on each proposal is computed server-side from its diff — never supplied by the generator. Only numeric and field-name changes in unprotected categories publish themselves.</p>
      </div>
    </Page>
  );
}
